import {
    Dialog,
    DialogPanel,
    DialogTitle,
} from "lib/headlessui/components/dialog/dialog";
import {
    Transition,
    TransitionChild,
} from "lib/headlessui/components/transition/transition";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { formatPhoneNumber, PhoneNumberE164, toPhoneNumberE164 } from "@sp-crm/core";
import { ContentFull, ContentHeader } from "components/layout";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    TwoFactorMethod,
    TwoFactorSetupInput,
    useDisableSpecificTwoFactorMethodMutation,
    useGetCurrentUserQuery,
    useSendTwoFactorCodeMutation,
    useSetupTwoFactorMutation,
    useVerifySetupCodeMutation,
} from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { useCurrentTenant } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";

interface PasswordConfirmDialogProps {
    title: string;
    prompt: string;
    confirmLabel: string;
    cancelLabel: string;
    onConfirm: (password: string) => void;
    onCancel: () => void;
}

const PasswordConfirmDialog: React.FC<PasswordConfirmDialogProps> = ({
    title,
    prompt,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onCancel,
}) => {
    const [password, setPassword] = useState("");
    const [isHidden, setIsHidden] = useState(false);

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            if (password) {
                onConfirm(password);
                setIsHidden(true);
            }
        },
        [password, onConfirm],
    );

    const handleCancel = useCallback(() => {
        onCancel();
        setIsHidden(true);
    }, [onCancel]);

    const handlePasswordChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value),
        [],
    );

    return (
        <Transition show={!isHidden} appear={true}>
            <Dialog as="div" className="relative z-1500" onClose={handleCancel}>
                <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </TransitionChild>

                <div className="fixed z-1500 inset-0 overflow-y-auto">
                    <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
                        <TransitionChild
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                                <form onSubmit={handleSubmit}>
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationTriangleIcon
                                                className="h-6 w-6 text-red-600"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <DialogTitle
                                                as="h3"
                                                className="text-lg leading-6 font-medium text-gray-900">
                                                {title}
                                            </DialogTitle>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500 mb-4">
                                                    {prompt}
                                                </p>
                                                <Input
                                                    type="password"
                                                    value={password}
                                                    onChange={handlePasswordChange}
                                                    label="Password"
                                                    placeholder="Enter your password"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <PrimaryButton type="submit" disabled={!password}>
                                            {confirmLabel}
                                        </PrimaryButton>
                                        <SecondaryButton
                                            type="button"
                                            onClick={handleCancel}>
                                            {cancelLabel}
                                        </SecondaryButton>
                                    </div>
                                </form>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

const passwordConfirm = async (
    title: string,
    prompt: string,
    confirmLabel: string,
    cancelLabel: string,
): Promise<string | null> => {
    const wrapper = document.body.appendChild(document.createElement("div"));
    const root = ReactDOM.createRoot(wrapper);
    const promise = new Promise<string | null>(resolve => {
        const onConfirm = (password: string) => resolve(password);
        const onCancel = () => resolve(null);
        root.render(
            <PasswordConfirmDialog
                title={title}
                prompt={prompt}
                confirmLabel={confirmLabel}
                cancelLabel={cancelLabel}
                onConfirm={onConfirm}
                onCancel={onCancel}
            />,
        );
    });
    const result = await promise;
    root.unmount();
    setTimeout(() => wrapper.remove());
    return result;
};

interface ComponentState {
    isProcessingEmail: boolean;
    isProcessingSms: boolean;
    isVerifyingCode: boolean;
    successMessage: string | null;
    errorMessage: string | null;
    showSetupFlow: boolean;
    setupStep: "initial" | "verify" | "add-phone-number";
    setupMethod: TwoFactorMethod | null;
    verificationCode: string;
    phoneNumber: PhoneNumberE164;
    resendCountdown: number;
}

type Action =
    | { type: "setErrorMessage"; message: string | null }
    | { type: "setShowSetupFlow"; value: boolean }
    | { type: "setSetupStep"; step: "initial" | "verify" | "add-phone-number" }
    | { type: "setVerificationCode"; code: string }
    | { type: "setPhoneNumber"; number: PhoneNumberE164 }
    | { type: "startEmailSetup" }
    | { type: "endEmailSetup" }
    | { type: "startSmsSetup" }
    | { type: "endSmsSetup" }
    | { type: "startVerification" }
    | { type: "endVerification" }
    | { type: "startDisableEmail" }
    | { type: "endDisableEmail" }
    | { type: "startDisableSms" }
    | { type: "endDisableSms" }
    | { type: "startAddPhoneNumber" }
    | { type: "emailSetupSuccess" }
    | { type: "smsSetupSuccess" }
    | { type: "setupError"; message: string }
    | { type: "verificationSuccess"; message: string }
    | { type: "verificationError"; message: string }
    | { type: "resendSuccess"; message: string }
    | { type: "disableSuccess"; message: string }
    | { type: "disableError"; message: string }
    | { type: "cancelSetup" }
    | { type: "setResendCountdown"; countdown: number };

const reducer: React.Reducer<ComponentState, Action> = (p, a): ComponentState => {
    return produce(p, draft => {
        switch (a.type) {
            case "startEmailSetup":
                draft.isProcessingEmail = true;
                draft.errorMessage = null;
                break;
            case "endEmailSetup":
                draft.isProcessingEmail = false;
                break;
            case "startSmsSetup":
                draft.isProcessingSms = true;
                draft.errorMessage = null;
                break;
            case "endSmsSetup":
                draft.isProcessingSms = false;
                break;
            case "startVerification":
                draft.isVerifyingCode = true;
                break;
            case "endVerification":
                draft.isVerifyingCode = false;
                break;
            case "startDisableEmail":
                draft.isProcessingEmail = true;
                break;
            case "endDisableEmail":
                draft.isProcessingEmail = false;
                break;
            case "startDisableSms":
                draft.isProcessingSms = true;
                break;
            case "endDisableSms":
                draft.isProcessingSms = false;
                break;
            case "startAddPhoneNumber":
                draft.setupStep = "add-phone-number";
                draft.showSetupFlow = true;
                break;
            case "setErrorMessage":
                draft.errorMessage = a.message;
                break;
            case "setShowSetupFlow":
                draft.showSetupFlow = a.value;
                break;
            case "setSetupStep":
                draft.setupStep = a.step;
                break;
            case "setVerificationCode":
                draft.verificationCode = a.code;
                break;
            case "setPhoneNumber":
                draft.phoneNumber = a.number;
                break;
            case "emailSetupSuccess":
                draft.setupMethod = TwoFactorMethod.Email;
                draft.setupStep = "verify";
                draft.showSetupFlow = true;
                draft.successMessage =
                    "Verification code sent to your email. Please enter it below to complete setup.";
                draft.errorMessage = null;
                break;
            case "smsSetupSuccess":
                draft.setupMethod = TwoFactorMethod.Sms;
                draft.setupStep = "verify";
                draft.showSetupFlow = true;
                draft.successMessage =
                    "Verification code sent to your phone. Please enter it below to complete setup.";
                draft.errorMessage = null;
                break;
            case "setupError":
                draft.errorMessage = a.message;
                draft.successMessage = null;
                break;
            case "verificationSuccess":
                draft.showSetupFlow = false;
                draft.setupStep = "initial";
                draft.setupMethod = null;
                draft.verificationCode = "";
                draft.successMessage = a.message;
                draft.errorMessage = null;
                break;
            case "verificationError":
                draft.errorMessage = a.message;
                draft.successMessage = null;
                break;
            case "resendSuccess":
                draft.successMessage = a.message;
                draft.errorMessage = null;
                break;
            case "disableSuccess":
                draft.successMessage = a.message;
                draft.errorMessage = null;
                break;
            case "disableError":
                draft.errorMessage = a.message;
                draft.successMessage = null;
                break;
            case "cancelSetup":
                draft.showSetupFlow = false;
                draft.setupStep = "initial";
                draft.setupMethod = null;
                draft.verificationCode = "";
                draft.successMessage = null;
                draft.errorMessage = null;
                break;
            case "setResendCountdown":
                draft.resendCountdown = a.countdown;
                break;
        }
    });
};

const initialState: ComponentState = {
    isProcessingEmail: false,
    isProcessingSms: false,
    isVerifyingCode: false,
    successMessage: null,
    errorMessage: null,
    showSetupFlow: false,
    setupStep: "initial",
    setupMethod: null,
    verificationCode: "",
    phoneNumber: toPhoneNumberE164(""),
    resendCountdown: 0,
};

export const AccountSettings2FA: React.FC<unknown> = () => {
    const currentUserQuery = useGetCurrentUserQuery({}, { keepPreviousData: true });
    const setupMutation = useSetupTwoFactorMutation();
    const verifyMutation = useVerifySetupCodeMutation();
    const disableSpecificMutation = useDisableSpecificTwoFactorMethodMutation();
    const sendCodeMutation = useSendTwoFactorCodeMutation();

    const [state, dispatch] = React.useReducer(reducer, initialState);
    const tenant = useCurrentTenant();

    const user = currentUserQuery.data?.getCurrentUser;
    const hasPhoneNumber = Boolean(user?.phone);
    const isEmailSetup = user?.twoFactorEmailEnabled || false;
    const isSmsSetup = user?.twoFactorSmsEnabled || false;
    const isBaaEnforced = tenant?.baaEnforced || false;

    const handleSetupEmail2FA = async () => {
        const password = await passwordConfirm(
            "Setup Email Two-Factor Authentication",
            "Enter your password to enable email two-factor authentication:",
            "Setup",
            "Cancel",
        );

        if (!password) return;

        handleEvent("2fa-enable-start", { method: TwoFactorMethod.Email });
        dispatch({ type: "startEmailSetup" });
        try {
            const input: TwoFactorSetupInput = {
                method: TwoFactorMethod.Email,
                password,
            };

            const result = await setupMutation.mutateAsync({
                input,
            });

            if (result.setupTwoFactor.success) {
                handleEvent("2fa-enable-success", { method: TwoFactorMethod.Email });
                dispatch({ type: "emailSetupSuccess" });
                dispatch({ type: "setResendCountdown", countdown: 30 });
            }
        } catch (error) {
            handleEvent("2fa-enable-fail", { method: TwoFactorMethod.Email });
            dispatch({
                type: "setupError",
                message: "Failed to enable two-factor authentication with email",
            });
        } finally {
            dispatch({ type: "endEmailSetup" });
        }
    };

    const handleSetupSMS2FA = useCallback(async () => {
        const password = await passwordConfirm(
            "Setup SMS Two-Factor Authentication",
            "Enter your password to enable SMS two-factor authentication:",
            "Setup",
            "Cancel",
        );

        if (!password) return;

        handleEvent("2fa-enable-start", { method: TwoFactorMethod.Sms });
        dispatch({ type: "startSmsSetup" });
        try {
            const input: TwoFactorSetupInput = {
                method: TwoFactorMethod.Sms,
                phoneNumber:
                    (user?.phone ? toPhoneNumberE164(user.phone) : undefined) ||
                    (state.phoneNumber
                        ? toPhoneNumberE164(state.phoneNumber)
                        : undefined),
                password,
            };

            const result = await setupMutation.mutateAsync({
                input,
            });

            if (result.setupTwoFactor.success) {
                handleEvent("2fa-enable-success", { method: TwoFactorMethod.Sms });
                dispatch({ type: "smsSetupSuccess" });
                dispatch({ type: "setResendCountdown", countdown: 30 });
            }
        } catch (error) {
            handleEvent("2fa-enable-fail", { method: TwoFactorMethod.Sms });
            dispatch({
                type: "setupError",
                message: "Failed to enable two-factor authentication with SMS",
            });
        } finally {
            dispatch({ type: "endSmsSetup" });
        }
    }, [state.phoneNumber, setupMutation, user?.phone, dispatch]);

    const handleAddPhoneAndSetup2FA = useCallback(async () => {
        if (!state.phoneNumber) {
            dispatch({ type: "setErrorMessage", message: "Please enter a phone number" });
            return;
        }

        const password = await passwordConfirm(
            "Setup SMS Two-Factor Authentication",
            "Enter your password to add phone number and enable SMS two-factor authentication:",
            "Setup",
            "Cancel",
        );

        if (!password) return;

        handleEvent("2fa-enable-start", { method: TwoFactorMethod.Sms });
        dispatch({ type: "startSmsSetup" });
        try {
            const input: TwoFactorSetupInput = {
                method: TwoFactorMethod.Sms,
                phoneNumber: toPhoneNumberE164(state.phoneNumber),
                password,
            };

            const result = await setupMutation.mutateAsync({
                input,
            });

            if (result.setupTwoFactor.success) {
                handleEvent("2fa-enable-success", { method: TwoFactorMethod.Sms });
                dispatch({ type: "smsSetupSuccess" });
                dispatch({ type: "setResendCountdown", countdown: 30 });
            }
        } catch (error) {
            handleEvent("2fa-enable-fail", { method: TwoFactorMethod.Sms });
            dispatch({
                type: "setupError",
                message: "Failed to add phone number or enable two-factor authentication",
            });
        } finally {
            dispatch({ type: "endSmsSetup" });
        }
    }, [state.phoneNumber, setupMutation, dispatch]);

    const handleVerifySetupCode = useCallback(async () => {
        if (!state.verificationCode || !state.setupMethod) {
            dispatch({
                type: "setErrorMessage",
                message: "Please enter the verification code",
            });
            return;
        }

        dispatch({ type: "startVerification" });
        try {
            const result = await verifyMutation.mutateAsync({
                input: {
                    code: state.verificationCode,
                    method: state.setupMethod!,
                },
            });

            if (result.verifySetupCode.success) {
                handleEvent("2fa-enable-success", { method: state.setupMethod });
                dispatch({
                    type: "verificationSuccess",
                    message:
                        result.verifySetupCode.message ||
                        `Two-factor authentication enabled successfully with ${
                            state.setupMethod === "Sms" ? "SMS" : "email"
                        }`,
                });
                currentUserQuery.refetch();
            } else {
                handleEvent("2fa-enable-fail", { method: state.setupMethod });
                dispatch({
                    type: "verificationError",
                    message:
                        result.verifySetupCode.message || "Invalid verification code",
                });
            }
        } catch (error) {
            handleEvent("2fa-enable-fail", { method: state.setupMethod });
            dispatch({ type: "verificationError", message: "Failed to verify code" });
        } finally {
            dispatch({ type: "endVerification" });
        }
    }, [
        state.verificationCode,
        state.setupMethod,
        verifyMutation,
        currentUserQuery,
        dispatch,
    ]);

    const handleDisableEmail2FA = useCallback(async () => {
        const password = await passwordConfirm(
            "Disable Email Two-Factor Authentication?",
            "Enter your password to disable email two-factor authentication:",
            "Yes, disable",
            "Cancel",
        );

        if (!password) return;

        handleEvent("2fa-disable-start", { method: TwoFactorMethod.Email });
        dispatch({ type: "startDisableEmail" });
        try {
            await disableSpecificMutation.mutateAsync({
                method: TwoFactorMethod.Email,
                password,
            });
            handleEvent("2fa-disable-success", { method: TwoFactorMethod.Email });
            dispatch({
                type: "disableSuccess",
                message: "Email two-factor authentication has been disabled.",
            });
            currentUserQuery.refetch();
        } catch (error) {
            handleEvent("2fa-disable-fail", { method: TwoFactorMethod.Email });
            dispatch({
                type: "disableError",
                message: "Failed to disable email two-factor authentication",
            });
        } finally {
            dispatch({ type: "endDisableEmail" });
        }
    }, [disableSpecificMutation, currentUserQuery, dispatch]);

    const handleDisableSMS2FA = useCallback(async () => {
        const password = await passwordConfirm(
            "Disable SMS Two-Factor Authentication?",
            "Enter your password to disable SMS two-factor authentication:",
            "Yes, disable",
            "Cancel",
        );

        if (!password) return;

        handleEvent("2fa-disable-start", { method: TwoFactorMethod.Sms });
        dispatch({ type: "startDisableSms" });
        try {
            await disableSpecificMutation.mutateAsync({
                method: TwoFactorMethod.Sms,
                password,
            });
            handleEvent("2fa-disable-success", { method: TwoFactorMethod.Sms });
            dispatch({
                type: "disableSuccess",
                message: "SMS two-factor authentication has been disabled.",
            });
            currentUserQuery.refetch();
        } catch (error) {
            handleEvent("2fa-disable-fail", { method: TwoFactorMethod.Sms });
            dispatch({
                type: "disableError",
                message: "Failed to disable SMS two-factor authentication",
            });
        } finally {
            dispatch({ type: "endDisableSms" });
        }
    }, [disableSpecificMutation, currentUserQuery, dispatch]);

    const handleCancelSetup = useCallback(() => {
        dispatch({ type: "cancelSetup" });
    }, [dispatch]);

    useEffect(() => {
        if (state.resendCountdown > 0) {
            const timer = setTimeout(() => {
                dispatch({
                    type: "setResendCountdown",
                    countdown: state.resendCountdown - 1,
                });
            }, 1000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [state.resendCountdown]);

    const handleResendCode = useCallback(async () => {
        if (!state.setupMethod || state.resendCountdown > 0) return;

        try {
            await sendCodeMutation.mutateAsync({
                input: { method: state.setupMethod },
            });
            handleEvent("2fa-enable-resent", { method: state.setupMethod });
            dispatch({ type: "setResendCountdown", countdown: 30 });
            dispatch({
                type: "resendSuccess",
                message: `Verification code resent to your ${
                    state.setupMethod === TwoFactorMethod.Sms ? "phone" : "email"
                }.`,
            });
        } catch (error) {
            handleEvent("2fa-enable-fail", { method: state.setupMethod });
            dispatch({
                type: "verificationError",
                message: "Failed to resend verification code. Please try again.",
            });
        }
    }, [state.setupMethod, state.resendCountdown, sendCodeMutation]);

    if (!user) {
        return <div>Loading...</div>;
    }

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Two-Factor Authentication</ContentHeader>
                <div className="space-y-4">
                    <div className="text-gray-600">
                        Two-factor authentication adds an extra layer of security to your
                        account by requiring a verification code in addition to your
                        password when signing in.
                    </div>

                    {isBaaEnforced && isEmailSetup && (
                        <InlineBanner type="info">
                            Your organization requires email two-factor authentication for
                            security compliance.
                        </InlineBanner>
                    )}

                    <div className="space-y-6">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 border border-gray-100 rounded">
                                <div className="flex items-center space-x-3">
                                    <div>
                                        <h4 className="font-medium">Email</h4>
                                        <p className="text-sm text-gray-600">
                                            Send code to {user?.email}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    {isEmailSetup ? (
                                        <div className="flex items-center space-x-3">
                                            <div className="flex items-center text-green-600">
                                                <svg
                                                    className="w-5 h-5 mr-1"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                <span className="text-sm font-medium">
                                                    Email enabled
                                                    {isBaaEnforced ? " (Required)" : ""}
                                                </span>
                                            </div>
                                            {!isBaaEnforced && (
                                                <SecondaryButton
                                                    onClick={handleDisableEmail2FA}
                                                    disabled={state.isProcessingEmail}
                                                    className="text-sm">
                                                    {state.isProcessingEmail
                                                        ? "Disabling..."
                                                        : "Disable"}
                                                </SecondaryButton>
                                            )}
                                        </div>
                                    ) : (
                                        <PrimaryButton
                                            onClick={handleSetupEmail2FA}
                                            disabled={state.isProcessingEmail}
                                            className="text-sm">
                                            {state.isProcessingEmail
                                                ? "Setting up..."
                                                : "Setup with Email"}
                                        </PrimaryButton>
                                    )}
                                </div>
                            </div>

                            {/* SMS Setup Status */}
                            <div className="flex items-center justify-between p-3 border border-gray-100 rounded">
                                <div className="flex items-center space-x-3">
                                    <div>
                                        <h4 className="font-medium">SMS</h4>
                                        <p className="text-sm text-gray-600">
                                            {hasPhoneNumber
                                                ? `Send code to ${formatPhoneNumber(
                                                      user?.phone,
                                                  )}`
                                                : "Add a phone number to enable SMS verification"}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    {isSmsSetup ? (
                                        <div className="flex items-center space-x-3">
                                            <div className="flex items-center text-green-600">
                                                <svg
                                                    className="w-5 h-5 mr-1"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                <span className="text-sm font-medium">
                                                    SMS enabled
                                                </span>
                                            </div>
                                            <SecondaryButton
                                                onClick={handleDisableSMS2FA}
                                                disabled={state.isProcessingSms}
                                                className="text-sm">
                                                {state.isProcessingSms
                                                    ? "Disabling..."
                                                    : "Disable"}
                                            </SecondaryButton>
                                        </div>
                                    ) : (
                                        <PrimaryButton
                                            onClick={
                                                hasPhoneNumber
                                                    ? handleSetupSMS2FA
                                                    : () => {
                                                          dispatch({
                                                              type: "startAddPhoneNumber",
                                                          });
                                                      }
                                            }
                                            disabled={state.isProcessingSms}
                                            className="text-sm">
                                            {state.isProcessingSms
                                                ? "Setting up..."
                                                : hasPhoneNumber
                                                  ? "Setup with SMS"
                                                  : "Add Phone Number"}
                                        </PrimaryButton>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {state.showSetupFlow && state.setupStep === "add-phone-number" && (
                        <div className="mt-6 p-4 border border-gray-200 rounded">
                            <h3 className="font-semibold text-lg mb-4">
                                Add Phone Number for SMS
                            </h3>
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    Add a phone number to your account to enable SMS
                                    verification
                                </p>
                                <PhoneInputE164
                                    initial={state.phoneNumber}
                                    onCommit={pn =>
                                        dispatch({
                                            type: "setPhoneNumber",
                                            number: pn,
                                        })
                                    }
                                    label="Phone Number"
                                    placeholder="Enter your phone number (US only)"
                                />
                                <div className="flex justify-end space-x-3">
                                    <SecondaryButton onClick={handleCancelSetup}>
                                        Cancel
                                    </SecondaryButton>
                                    <PrimaryButton
                                        onClick={handleAddPhoneAndSetup2FA}
                                        disabled={
                                            state.isProcessingSms || !state.phoneNumber
                                        }>
                                        {state.isProcessingSms
                                            ? "Setting up..."
                                            : "Add Phone & Setup SMS"}
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>
                    )}
                    {state.successMessage ? (
                        <InlineBanner type="success">{state.successMessage}</InlineBanner>
                    ) : null}

                    {state.showSetupFlow && state.setupStep === "verify" && (
                        <div className="mt-6 p-4 border border-gray-200 rounded">
                            <h3 className="font-semibold text-lg mb-4">
                                Complete Two-Factor Authentication Setup
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Enter the verification code sent to your{" "}
                                        {state.setupMethod === "Sms" ? "phone" : "email"}{" "}
                                        to complete setup:
                                    </p>

                                    <Input
                                        type="number"
                                        value={state.verificationCode}
                                        onChange={e =>
                                            dispatch({
                                                type: "setVerificationCode",
                                                code: e.target.value,
                                            })
                                        }
                                        label="Verification Code"
                                        placeholder="Enter 6-digit code"
                                        className="mb-4"
                                        maxLength={6}
                                    />
                                </div>

                                <div className="flex justify-between items-center">
                                    <SecondaryButton
                                        onClick={handleResendCode}
                                        disabled={state.resendCountdown > 0}
                                        className="text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed">
                                        {state.resendCountdown > 0
                                            ? `Resend Code (${state.resendCountdown}s)`
                                            : "Resend Code"}
                                    </SecondaryButton>

                                    <div className="flex space-x-3">
                                        <SecondaryButton onClick={handleCancelSetup}>
                                            Cancel
                                        </SecondaryButton>
                                        <PrimaryButton
                                            onClick={handleVerifySetupCode}
                                            disabled={
                                                state.isVerifyingCode ||
                                                state.verificationCode.length !== 6
                                            }>
                                            {state.isVerifyingCode
                                                ? "Verifying..."
                                                : "Verify & Enable"}
                                        </PrimaryButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {state.errorMessage && (
                        <InlineBanner type="error">{state.errorMessage}</InlineBanner>
                    )}
                </div>
            </ContentFull>
        </div>
    );
};
